<template>
  <div class="step-dynamic__week-tab">
    <div v-if="weekLoader" class="step-dynamic__loader">
      <Loader size="24" />
    </div>
    <div v-else>
      <div class="swiper week-swiper" ref="weeklySwiper">
        <div class="month-header">
          <Button
            class="swiper-button-prev month__button"
            shape="circle"
            size="lg"
            variant="clear"
            iconLeft="left"
            :disabled="isPrevWeekDisabled"
          />
          <Button
            class="swiper-button-next month__button"
            variant="clear"
            shape="circle"
            size="lg"
            iconLeft="right"
            :disabled="isNextWeekDisabled"
          />
        </div>
        <div class="swiper-wrapper">
          <div
            class="swiper-slide"
            v-for="(week, index) in weekData"
            :key="week.dateRangeLabel || index"
          >
            <div class="month-title">
              {{ week.dateRangeLabel }} • <span>{{ week.totalSteps.toLocaleString('ru-RU') }} {{ getStepWord(week.totalSteps) }}</span>
            </div>

            <WeekBarGraph :weekData="week.week" />
          </div>
        </div>
      </div>

      <!-- Информация о текущей неделе -->
    </div>
    <div class="step-dynamic__items">
      <div class="step-dynamic__item">
        <Icon name="walking" size="xl" />
        <div>
          <span  class="step-dynamic__item-value">{{ currentWeekData.kilometers || 0 }}</span>
          <span class="step-dynamic__item-unit">км</span>
        </div>
      </div>
      <div class="step-dynamic__item step-dynamic__item_position" @click="$emit('goRating')">
        <Icon name="trophy" size="xl" />
        <div>
          <span class="step-dynamic__item-unit">№</span>
          <span class="step-dynamic__item-value">{{ currentWeekData.position || 0 }}</span>
        </div>
      </div>
      <div class="step-dynamic__item">
        <img :src="coinImg" />
        <span class="step-dynamic__item-value">{{ currentWeekData.points || 0 }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Swiper from 'swiper';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import CoinImg from '@/assets/img/coin.svg';
import WeekBarGraph from '../WeekBarGraph/WeekBarGraph.vue';

dayjs.extend(isBetween);
dayjs.extend(isoWeek);

export default {
  name: 'WeekTab',
  components: {
    WeekBarGraph,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      weekData: [
        {
          loading: true, week: [], dateRangeLabel: '', totalSteps: 0, kilometers: 0, position: 0, points: 0,
        },
        {
          loading: true, week: [], dateRangeLabel: '', totalSteps: 0, kilometers: 0, position: 0, points: 0,
        },
        {
          loading: true, week: [], dateRangeLabel: '', totalSteps: 0, kilometers: 0, position: 0, points: 0,
        },
      ],
      weekLoader: false,
      swiperWeek: null,
      coinImg: CoinImg,
      baseWeek: dayjs().startOf('isoWeek'),
      slideIndex: 1, // центральная неделя
    };
  },
  computed: {
    ...mapGetters('steps', ['stepsHistory', 'participantPositionData']),
    ...mapGetters('user', ['profile']),
    currentWeekData() {
      return this.weekData[1] || {};
    },
    isPrevWeekDisabled() {
      if (!this.profile?.createdOn || !this.weekData[0]) return false;
      // Начало недели, которую увидим при свайпе влево
      const prevWeekStart = dayjs(this.weekData[0].dateFrom).startOf('day');
      const prevWeekEnd = prevWeekStart.clone().add(6, 'day');
      const created = dayjs(this.profile.createdOn).startOf('day');
      // Если вся неделя до регистрации — дизейблим
      return prevWeekEnd.isBefore(created, 'day');
    },
    isNextWeekDisabled() {
      // Блокируем свайп вправо, если следующая неделя начинается после сегодняшнего дня
      const week = this.weekData[2];
      if (!week || !week.dateFrom) return false;
      const startOfNextWeek = dayjs(week.dateFrom);
      const now = dayjs();
      return startOfNextWeek.isAfter(now, 'isoWeek');
    },
  },
  methods: {
    ...mapActions('steps', ['getPeriodSteps', 'getParticipantPositionData', 'getStepsHistory']),
    getStepWord(count) {
      const mod10 = count % 10;
      const mod100 = count % 100;
      if (mod10 === 1 && mod100 !== 11) return 'шаг';
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'шага';
      return 'шагов';
    },
    async fetchWeekData(dateFrom) {
      const startOfWeek = dayjs(dateFrom).startOf('isoWeek');
      const endOfWeek = startOfWeek.clone().add(6, 'day');
      await this.getParticipantPositionData({
        dateFrom: startOfWeek.format('YYYY-MM-DD'),
        dateTo: endOfWeek.format('YYYY-MM-DD'),
      });
      await this.getStepsHistory({
        dateFrom: startOfWeek.format('YYYY-MM-DD'),
        dateTo: endOfWeek.format('YYYY-MM-DD'),
        periodType: 'Weekly',
      });
      const stepsArray = this.stepsHistory || [];
      const stepsByDate = stepsArray.reduce((acc, day) => {
        acc[day.date] = {
          date: day.date,
          stepsCount: day.stepsCount,
          targetCount: day.targetCount,
        };
        return acc;
      }, {});
      const week = [];
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < 7; i++) {
        const date = startOfWeek.clone().add(i, 'day').format('YYYY-MM-DD');
        week.push(stepsByDate[date] || { date, stepsCount: 0, targetCount: 0 });
      }
      const totalSteps = week.reduce((sum, day) => sum + (day.stepsCount || 0), 0);
      const dateFromFormatted = startOfWeek.format('DD.MM');
      const dateToFormatted = endOfWeek.format('DD.MM');
      return {
        dateFrom: startOfWeek.format('YYYY-MM-DD'),
        dateTo: endOfWeek.format('YYYY-MM-DD'),
        week,
        position: this.participantPositionData?.position,
        points: this.participantPositionData?.accruedPoints,
        kilometers: this.participantPositionData?.kilometers,
        totalSteps,
        dateRangeLabel: `${dateFromFormatted}–${dateToFormatted}`,
      };
    },
    async initThreeWeeks(baseDate) {
      this.weekLoader = true;
      const prev = await this.fetchWeekData(dayjs(baseDate).subtract(1, 'week'));
      const curr = await this.fetchWeekData(dayjs(baseDate));
      const next = await this.fetchWeekData(dayjs(baseDate).add(1, 'week'));
      this.weekData = [prev, curr, next];
      this.weekLoader = false;
    },
    async handleSlideChange() {
      if (!this.swiperWeek) return;
      const { realIndex } = this.swiperWeek;
      if (realIndex === 0) {
        const newPrev = await this.fetchWeekData(dayjs(this.weekData[0].dateFrom).subtract(1, 'week'));
        this.weekData.pop();
        this.weekData.unshift(newPrev);
      } else if (realIndex === 2) {
        const newNext = await this.fetchWeekData(dayjs(this.weekData[2].dateFrom).add(1, 'week'));
        this.weekData.shift();
        this.weekData.push(newNext);
      }
      this.$nextTick(() => {
        if (this.swiperWeek) {
          this.swiperWeek.update();
          this.swiperWeek.slideTo(1, 0);
          this.updateSwipeLock();
        }
      });
    },
    initSwiper() {
      this.swiperWeek = new Swiper(this.$refs.weeklySwiper, {
        slidesPerView: 1,
        spaceBetween: 10,
        allowTouchMove: true,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        initialSlide: 1,
        on: {
          slideChange: this.handleSlideChange,
        },
      });
    },
    updateSwipeLock() {
      if (!this.swiperWeek) return;
      this.swiperWeek.allowSlidePrev = !this.isPrevWeekDisabled;
      this.swiperWeek.allowSlideNext = !this.isNextWeekDisabled;
      // Если обе стороны заблокированы — полностью запрещаем свайп
      this.swiperWeek.allowTouchMove = this.swiperWeek.allowSlidePrev || this.swiperWeek.allowSlideNext;
      this.swiperWeek.update();
    },
  },
  async mounted() {
    await this.initThreeWeeks(this.baseWeek);
    this.$nextTick(() => {
      this.initSwiper();
      this.updateSwipeLock();
    });
  },
  watch: {
    weekData: {
      handler() {
        this.updateSwipeLock();
      },
      deep: true,
    },
    isPrevWeekDisabled() {
      this.updateSwipeLock();
    },
    isNextWeekDisabled() {
      this.updateSwipeLock();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './StepDynamicModal';
.month-title {
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 5%;
  text-transform: uppercase;
  color: $color-black-45;
  position: absolute;
  top: -23%;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  span {
    color: $color-black-85;
  }
  @media(max-width: 998px) {
    font-size: 12px;
    top: -18%;
  }
}
.month-header {
  margin: 20px -16px;
  padding: 0 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
  @media (max-width: 998px) {
    justify-content: center;
    height: 50px;
  }
  .month__button {
    position: relative;
    top: 50%;
    transform: translateY(-50%);
    width: 48px;
    height: 48px;
    background: $color-white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 1px rgba(0, 0, 0, 0.08);
    border-radius: 50% !important;
    outline: none;
    line-height: 1;
    z-index: 2;
    transition: 0.2s ease-in-out;
    color: $color-black-85;
    &.disabled {
      opacity: 0;
    }
    @media (max-width: 998px) {
      display: none;
    }
  }
}
.week-title {
  position: absolute;
  top: -65px;
  left: 0;
  right: 0;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 5%;
  color: $color-black-45;
  span {
    color: $color-black-85;
  }
}
.month-slider {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 !important;
}

.month-day {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.day-info {
  margin-top: 10px;
  text-align: center;
}
.weeks-slider {
  padding-top: 90px !important;
  overflow-y: visible !important;
  ::v-deep.rr-button.swiper-button-prev {
    left: 1px;
  }
  ::v-deep.rr-button.swiper-button-next {
    right: 10px;
    top: -90px;
  }
  ::v-deep.rr-button.swiper-button-prev {
    left: 10px;
    top: -90px;
  }
}
.week-swiper {
  overflow: hidden;
}
.step-dynamic__weeks {
  position: relative;
  // padding: 30px 0;
  display: flex;
  flex-direction: column;

}
.step-dynamic__title {
  position: absolute;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 5%;
  span {
    color: rgba(1, 1, 1, 0.45);
    text-transform: uppercase;
  }
}
.step-dynamic__progress-container {
  display: flex;
  width: 342px;
}

.step-dynamic__week {
  display: flex;
}

.step-dynamic__week-step,
.step-dynamic__week-target,
.step-dynamic__week-day {
  text-align: center;
  letter-spacing: 5%;
  line-height: 18px;
  color: rgba(1, 1, 1, 0.45);
}
.step-dynamic__week-target {
  font-size: 6px;
}
.step-dynamic__week-step {
  font-size: 8px;
  font-weight: 600;
  margin-top: -20px;
}
.step-dynamic__week-day {
  font-size: 12px;
  font-weight: 600;
  margin-top: 8px;
}

.step-dynamic__goal-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(1, 1, 1, 0.04); /* Цвет цели (серая) */
  border-radius: 8px 8px 0 0;
  z-index: 2;
}

.step-dynamic__progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  transition: height 0.3s ease; /* Плавный переход высоты */
  border-radius: 8px 8px 0 0;
  z-index: 1;
}
.month-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 10px;
}
.loading-indicator {
  width: 100%;
  height: 232px;
}
.hidden-slide {
  opacity: 0;
  pointer-events: none; /* чтобы не было кликов */
  transition: opacity 0.3s;
}
  .swiper-button-next,
  .swiper-button-prev {
    // display: none;
    flex: 0 0 auto;
    width: 48px;
    height: 48px;
    background: $color-white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 1px rgba(0, 0, 0, 0.08);
    border-radius: 50% !important;
    outline: none;
    position: absolute;
    line-height: 1;
    z-index: 2;
    transition: 0.2s ease-in-out;
    color: $color-black-85;

    &:hover {
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.08);
    }

    svg {
      fill: currentColor;
    }

    @media (max-width: 866px) {
      margin-left: 0;
      margin-right: 0;
    }
  }
  .step-dynamic__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 390px;
  }

.day-info {
  margin-top: 10px;
  text-align: center;
}
.weeks-slider {
  padding-top: 90px !important;
  overflow-y: visible !important;
  ::v-deep.rr-button.swiper-button-prev {
    left: 1px;
  }
  ::v-deep.rr-button.swiper-button-next {
    right: 10px;
    top: -90px;
  }
  ::v-deep.rr-button.swiper-button-prev {
    left: 10px;
    top: -90px;
  }
}
</style>
